/**
 * The author's own site, and a mark on the link when it has changed.
 *
 * The mark is the whole feature. It is a hollow ring rather than a filled dot
 * because filled already means "we know this happened" — a date moved, a book
 * appeared, something a catalogue vouched for. This one means "a page you care
 * about is not what it was, and we have no idea whether that matters". Hollow
 * is the app's word for not-yet-known, the same as the empty slot in a series
 * fan. No count, ever: counting would claim we judged which changes mattered.
 *
 * It clears on click and not on leaving the screen, because the only thing it
 * asserts is that the reader has not been since it changed.
 */

import { useEffect, useRef, useState } from 'react';
import type { SiteState } from '../client.ts';
import { changedSince } from '../../site/index.js';
import type { WatchItem } from '../lib/model.js';

interface Props {
  item: WatchItem;
  /** Save an address, or clear it with null. Looks immediately either way. */
  onSet: (url: string | null) => Promise<SiteState | null>;
  /** Look now. The worker declines if it looked within the hour. */
  onCheck: () => Promise<SiteState | null>;
  onSeen: () => void;
}

export function SiteWatch({ item, onSet, onCheck, onSeen }: Props) {
  const [state, setState] = useState<SiteState | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [seen, setSeen] = useState(false);

  const url = state?.url ?? item.siteUrl;
  const changedAt = state ? state.changedAt : (item.siteCursor?.changedAt ?? null);

  // Held in a ref because the caller passes a fresh closure on every render,
  // and depending on it would check the site in a loop.
  const check = useRef(onCheck);
  check.current = onCheck;

  // Checked on open rather than nightly: this reads prose, and the poll path
  // is where dates are written. The cooldown lives on the server, so flicking
  // back to a page costs nothing.
  useEffect(() => {
    if (!item.siteUrl) return;
    let live = true;
    void check
      .current()
      .then((fresh) => {
        if (live && fresh) setState(fresh);
      })
      .catch(() => {
        // A site that will not answer is not worth an error message on a page
        // somebody opened to read a blurb.
      });
    return () => {
      live = false;
    };
  }, [item.watchableId, item.siteUrl]);

  async function save(next: string | null) {
    setBusy(true);
    setSaid(null);
    try {
      const saved = await onSet(next);
      setState(saved);
      setEditing(false);
      setDraft('');
      // No confirmation line here: once `url` is set, the permanent hint
      // below already states the mode — saying it twice is the bug this
      // fixes, not a feature.
    } catch (e) {
      setSaid(e instanceof Error ? e.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  if (!url) {
    if (!editing) {
      return (
        <button type="button" className="sitewatch-add" onClick={() => setEditing(true)}>
          Watch the author's site
        </button>
      );
    }
    return (
      <div className="sitewatch">
        <input
          className="sitewatch-input"
          type="url"
          inputMode="url"
          placeholder="https://example.com"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && draft.trim()) void save(draft.trim());
            if (e.key === 'Escape') setEditing(false);
          }}
        />
        <div className="sitewatch-row">
          <button
            type="button"
            className="action"
            disabled={busy || !draft.trim()}
            onClick={() => void save(draft.trim())}
          >
            {busy ? 'Looking…' : 'Watch it'}
          </button>
          <button type="button" className="linkish" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </div>
        {said && <p className="hint">{said}</p>}
      </div>
    );
  }

  const unseen = !seen && changedSince(changedAt, item.siteSeenAt);

  return (
    <div className="sitewatch">
      <div className="sitewatch-row">
        <a
          className="action"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            setSeen(true);
            onSeen();
          }}
        >
          {label(item)} ↗{unseen && <span className="sitedot" aria-hidden="true" />}
        </a>
        <button type="button" className="linkish" onClick={() => void save(null)} disabled={busy}>
          Stop watching it
        </button>
      </div>

      {/* Said plainly, because "no feed" is the difference between knowing what
          changed and knowing only that something did. */}
      <p className="hint">
        {unseen ? 'Changed since you last looked. ' : ''}
        {modeLine(state) || (item.siteCursor ? watchingLine(item.siteCursor.mode) : 'Not checked yet.')}
      </p>
      {said && <p className="hint">{said}</p>}
    </div>
  );
}

function label(item: WatchItem): string {
  if (item.kind === 'creator') return item.medium === 'book' ? "Author's site" : "Director's site";
  return 'Official site';
}

function watchingLine(mode: 'feed' | 'page'): string {
  return mode === 'feed'
    ? "Watching the site's feed."
    : 'No feed found — watching the page for any change.';
}

function modeLine(state: SiteState | null): string {
  if (!state) return '';
  if (state.unreachable) return 'Could not reach it just now. The last answer still stands.';
  const base = watchingLine(state.hasFeed ? 'feed' : 'page');
  // The headline is the site's own words, never ours about it.
  return state.latestTitle ? `${base} Latest: “${state.latestTitle}”.` : base;
}
